import React, { useEffect, useState, useRef, useContext, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  IconButton,
  Tooltip,
  TooltipProps,
  TextFieldProps,
  Collapse,
  Button,
  Chip,
} from '@mui/material';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import HistoryIcon from '@mui/icons-material/History';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { styled, useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import balaisIcon from '../../../../assets/BalaisH.svg';
import balaisIconHover from '../../../../assets/Balais.svg';
import groupIcon from '../../../../assets/Group.svg';
import groupIconHover from '../../../../assets/GroupH.svg';
import crossIcon from '../../../../assets/Cross.svg';
import crossIconHover from '../../../../assets/CrossH.svg';
import { Message, ChatMessage, ApiCitation } from './messages/ChatMessage';
import { SuggestedQuestions } from './SuggestedQuestions';
import { ChatHistoryPanel } from './ChatHistoryPanel';
import { SessionDivider } from './SessionDivider';
import { IndexingStatusBanner } from './IndexingStatusBanner';
import { useNavigate } from 'react-router-dom';
import { ChatAskBackendPayload, ChatBackendResponse, askChatQuestion, getSousDossierRagStatus, getSousDossierIndexingStatus, logChatError, voteMessage, getChatSessionsList, ChatSessionSummary } from '@/taskpane/services/chat';
import { useGetUserAttributesQuery } from '@/taskpane/services/auth.hook';
import { AuthContext } from '@/taskpane/contexts/auth/AuthContext';
import { getSubFolders } from '@/taskpane/services/folders';
import { DocumentTabProps } from './documents/DocumentsPage';
import { DocumentViewerPanel } from './documents/DocumentsPage';
import { isRunningInOutlook } from '@/taskpane/services/office';
import { apiClient } from '@/taskpane/utils/client';

// --- Styled Components ---

const PageContainer = styled(Box)(({ theme }) => ({
  marginTop: '10px',
  display: 'flex',
  width: '100%',
  height: '98%',
//   boxShadow: '4px 10px 12px rgba(0, 0, 0, 0.8)',
  paddingRight: theme.spacing(3),
  boxSizing: 'border-box',
}));

const ChatContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.default,
  boxShadow: '-4px 0px 12px rgba(0, 0, 0, 0.1)',
  transition: theme.transitions.create(['width', 'min-width', 'max-width', 'flex-basis', 'flex-grow', 'flex-shrink', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
}));

const Header = styled(Paper)(({ theme }) => ({
  padding: "0px 9px",
  background: 'white',
  borderBottom: '1px solid #E4E7EC',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  elevation: 0,
  width: '100%',
  borderRadius: '20px 20px 0px 0px',
  zIndex: 5,
}));

const TitleBlock = styled(Box)(({ theme }) => ({
  padding: "10px 0px",
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: "4px", // 4px
  width: '100%', // Example: ensure title doesn't overlap icons
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  color: 'black',
  fontSize: '15px',
  fontFamily: 'Inter, sans-serif',
  maxWidth: '300px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  fontWeight: '700',
  lineHeight: '1.22', // Approx for max-height 22px
}));

const HeaderSubtitle = styled(Typography)(({ theme }) => ({
  color: '#111827',
  fontSize: '14px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: '400',
  lineHeight: '1.21', // Approx for max-height 34px over 2 lines
  maxHeight: '34px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
}));

const HeaderIcons = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: "16px", // 16px
}));

const ChatArea = styled(Box)(({ theme }) => ({
  alignSelf: 'stretch',
  flex: '1 1 0',
  position: 'relative',
  background: 'linear-gradient(146deg, #F0F4FF 0%, white 100%)',
  overflowY: 'auto',
  padding: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
}));

const WelcomeMessageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minWidth: '350px',
  padding: '162px 36px',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '12px',
  flex: '1 0 0',
}));

const WelcomeTitle = styled(Typography)(({ theme }) => ({
    fontSize: '20px',
    fontFamily: 'Inter, sans-serif',
    fontWeight: '700',
    // background: 'linear-gradient(91deg, #2563EB 5.84%, #7C3AED 94.16%)',
    background: 'linear-gradient(92.29deg, #ff6136 0.46%, #ff875e 65.93%, #ffb399)',
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
}));

const WelcomeText = styled(Typography)(({ theme }) => ({
  alignSelf: 'stretch',
  color: '#4B5563',
  fontSize: '15px',
  fontFamily: 'Inter, sans-serif',
  fontStyle: 'normal',
  lineHeight: 'normal',
  textAlign: 'center',
  fontWeight: '400',
}));

const HistoryButton = styled(Button)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(3),
  padding: theme.spacing(1, 3),
  background: 'linear-gradient(90deg, rgba(135, 167, 255, 0.4) 0%, rgba(255, 180, 162, 0.4) 100%)',
  color: 'white',
  border: 'none',
  borderRadius: '50px',
  fontSize: '13px',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 500,
  textTransform: 'none',
  boxShadow: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  paddingLeft: theme.spacing(5),
  '&:hover': {
    background: 'linear-gradient(90deg, rgba(135, 167, 255, 0.5) 0%, rgba(255, 180, 162, 0.5) 100%)',
  },
  '&:disabled': {
    background: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
}));

const NewBadge = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  left: '-8px',
  top: '-8px',
  height: '20px',
  fontSize: '11px',
  fontWeight: 600,
  backgroundColor: '#FF6136',
  color: 'white',
  borderRadius: '4px',
  '& .MuiChip-label': {
    padding: '0 6px',
  },
}));

const InputArea = styled(Paper)(({ theme }) => ({
  alignSelf: 'stretch',
  padding: theme.spacing(1.5),
  background: 'white',
  borderTop: '1px solid #E4E7EC',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  borderRadius: '0px 0px 20px 20px',
  elevation: 0,
  height: 'auto',
}));

const MessageInput = styled(
  React.forwardRef<HTMLTextAreaElement, TextFieldProps>((props, ref) => (
    <TextField {...props} inputRef={ref} />
  ))
)(({ theme }) => ({
  flexGrow: 1,
  '& .MuiOutlinedInput-root': {
    borderRadius: '12px',
    padding: theme.spacing(1.25, 1.75),
    '& fieldset': {
      borderColor: '#E4E7EC',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '& textarea::placeholder': {
        fontStyle: 'italic',
        color: '#A4ACBE',
        fontSize: '15px',
        fontFamily: 'Inter, sans-serif',
        fontWeight: '400',
    },
  },
  '& .MuiInputBase-inputMultiline': {
    padding: '0px',
    boxSizing: 'border-box',
    resize: 'none',
  }
}));

const InlineSuggestionContainer = styled(Box)({
  position: 'relative',
  flexGrow: 1,
  display: 'flex',
});

const GhostText = styled('span')({
  color: 'rgba(0, 0, 0, 0.4)',
  pointerEvents: 'none',
  userSelect: 'none',
  fontStyle: 'italic',
});

interface InlineSuggestionInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onAcceptSuggestion: () => void;
  suggestion: string;
  disabled?: boolean;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  placeholder?: string;
}

const InlineSuggestionInput: React.FC<InlineSuggestionInputProps> = ({
  value,
  onChange,
  onSubmit,
  onAcceptSuggestion,
  suggestion,
  disabled = false,
  inputRef,
  placeholder,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' && suggestion) {
      e.preventDefault();
      onAcceptSuggestion();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onSubmit();
    }
  };

  return (
    <InlineSuggestionContainer>
      {suggestion && value && (
        <Box
          sx={{
            position: 'absolute',
            left: '14px',
            top: '10px',
            right: '50px',
            pointerEvents: 'none',
            zIndex: 1,
          }}
        >
          <Box
            component="span"
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              lineHeight: '1.5',
              whiteSpace: 'pre',
              color: 'transparent',
              display: 'inline-block',
              verticalAlign: 'top',
            }}
          >
            {value}
            <Box
              component="span"
              sx={{
                color: 'rgba(0, 0, 0, 0.4)',
                fontStyle: 'italic',
              }}
            >
              {suggestion}
            </Box>
          </Box>
        </Box>
      )}
      <MessageInput
        inputRef={inputRef}
        variant='outlined'
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        multiline
        minRows={1}
        maxRows={4}
        InputProps={{
          endAdornment: suggestion ? (
            <Box
              sx={{
                position: 'absolute',
                right: 8,
                bottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                backgroundColor: 'rgba(0, 0, 0, 0.06)',
                borderRadius: '4px',
                padding: '2px 6px',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  fontSize: '10px',
                  color: 'rgba(0, 0, 0, 0.5)',
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 500,
                }}
              >
                Tab
              </Typography>
            </Box>
          ) : null,
        }}
      />
    </InlineSuggestionContainer>
  );
};

const SendButton = styled(IconButton)<{ hastext?: string }>(({ theme, hastext }) => ({
  width: '35px',
  height: '35px',
  padding: 0,
  // background: hastext === 'true' ? 'linear-gradient(91deg, #2563EB 5.84%, #7C3AED 94.16%)' : 'linear-gradient(91deg, #E4E7EC 5.84%, #E4E7EC 94.16%)', // Figma colors
  background: hastext === 'true' ? '#3660F4' : 'linear-gradient(91deg, #E4E7EC 5.84%, #E4E7EC 94.16%)', // Figma colors
  borderRadius: '24px',
}));

const IconWrapper = styled(Box)({
    width: 24,
    height: 24,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': {
        maxWidth: '100%',
        maxHeight: '100%',
    }
});

interface HoverIconProps {
  defaultIconSrc: string;
  hoverIconSrc: string;
  altText: string;
  onClick?: () => void;
}

const HoverableActionIcon: React.FC<HoverIconProps> = ({
  defaultIconSrc,
  hoverIconSrc,
  altText,
  onClick,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme(); // Get theme for transitions

  return (
    <IconButton
      size="small"
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        padding: '4px',
        borderRadius: '50%',
        backgroundColor: isHovered
          ? 'linear-gradient(91deg, #2563EB 5.84%, #7C3AED 94.16%)'
          : 'transparent',
        filter: isHovered ? 'brightness(90%)' : 'none',
        transition: theme.transitions.create(['background-color', 'filter'], {
          duration: 0,
        }),
      }}
    >
      <IconWrapper>
        <img src={isHovered ? hoverIconSrc : defaultIconSrc} alt={altText} />
      </IconWrapper>
    </IconButton>
  );
};

// --- StyledTooltipIcon Component ---
interface StyledTooltipIconProps {
  tooltipTitle: string;
  placement?: TooltipProps['placement'];
  disabled?: boolean;
  children: React.ReactElement;
}

export const StyledTooltipIcon: React.FC<StyledTooltipIconProps> = ({
  tooltipTitle,
  placement = 'top',
  disabled = false,
  children,
}) => {
  const theme = useTheme();
  const gradient = 'linear-gradient(91deg, #2563EB 5.84%, #7C3AED 94.16%)';

  const tooltipContent = (
    <span
      style={{
        background: gradient,
        WebkitBackgroundClip: 'text',
        backgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        color: 'transparent',
      }}
    >
      {tooltipTitle}
    </span>
  );

  if (disabled) {
    return <span>{children}</span>;
  }

  return (
    <Tooltip
      title={tooltipContent}
      placement={placement}
      componentsProps={{
        tooltip: {
          sx: {
            fontSize: '10px',
            fontFamily: 'Public Sans, Inter, sans-serif',
            fontWeight: '500',
            lineHeight: '18px',
            padding: theme.spacing(0.25, 1),
            border: '1px solid transparent',
            borderRadius: '20px',
            background: `linear-gradient(white, white) padding-box, ${gradient} border-box`,
            backgroundClip: 'padding-box, border-box',
            backgroundOrigin: 'padding-box, border-box',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.25)',
          },
        },
      }}
    >
    <span>
      {children}
    </span>
    </Tooltip>
  );
};

interface AiChatPageProps {
  dossierTitle?: string;
  dossierSubtitle?: string;
}

interface SessionHistoryMessage {
  content: string;
  message_id: string;
  metadata: {
    tokens_used?: number;
    citations?: Array<{
      content: { type: string; text: string };
      location: { type: string; uri: string };
      metadata: { [key: string]: any };
      displayUrl?: string;
      text_span?: { start: number; end: number };
    }>;
  };
  role: 'user' | 'assistant';
  timestamp: string;
  vote_status: 'good' | 'bad';
}

interface SessionHistoryResponseData {
  client_id: string;
  created_on: string;
  dossier_id: string;
  messages: SessionHistoryMessage[];
  session_id: string;
  sous_dossier_id: string;
  status: string;
  updated_on: string;
  user_id: string;
}

// Function to generate a unique ID for a document tab based on URL and Page
const generateViewerTabId = (url: string, pageNumber?: number | string): string => {
  return `${url}#page=${pageNumber || 1}`; // Simplified ID for de-duplication
};

// The existing generateTabId can still be used if a more granular ID (including citationIndex)
// is needed for other purposes, but viewer tabs will use generateViewerTabId.
const generateTabId = (url: string, pageNumber?: number | string, citationIndex?: number) => {
  return `${url}#page=${pageNumber || 1}#citation=${citationIndex || 0}`;
};

// --- Autocomplete Suggestions ---
const AUTOCOMPLETE_SUGGESTIONS = [
  // Droit de la famille
  "régime matrimonial",
  "dévolution successorale",
  "actes d'état civil",
  "assurance-vie",
  "avoirs bancaires",
  "contrat de mariage",
  "testament",
  "donation",
  "héritiers",
  "légataire",
  "succession",
  "défunt",
  // Droit de l'immobilier
  "diagnostics immobiliers",
  "servitudes",
  "surface Carrez",
  "destination de l'immeuble",
  "dépenses énergétiques",
  "travaux",
  "bien vendu loué",
  "copropriété",
  "cadastre",
  "hypothèque",
  // Droit des affaires
  "gérants",
  "co-gérants",
  "répartition des parts sociales",
  "engagement de conservation",
  "capital social",
  "statuts",
  "associés",
  "société",
];

/**
 * Gets the inline completion suggestion for the last word being typed.
 * Returns only the remaining characters to complete the word.
 */
const getInlineSuggestion = (inputValue: string): string => {
  if (!inputValue || inputValue.endsWith(' ')) return '';

  const words = inputValue.split(' ');
  const lastWord = words[words.length - 1].toLowerCase();

  if (lastWord.length < 2) return '';

  const match = AUTOCOMPLETE_SUGGESTIONS.find((suggestion) =>
    suggestion.toLowerCase().startsWith(lastWord)
  );

  if (match) {
    return match.slice(lastWord.length);
  }

  return '';
};

export const mapSessionHistoryToMessages = (
  sessionData: SessionHistoryResponseData,
): Message[] => {
  if (!sessionData || !sessionData.messages) {
    return [];
  }

  return sessionData.messages.map((historyMsg: SessionHistoryMessage): Message => {
    const mappedCitations: ApiCitation[] | undefined = historyMsg.metadata.citations
      ? historyMsg.metadata.citations.map(cit => ({
          content: cit.content,
          location: cit.location,
          metadata: cit.metadata,
          displayUrl: cit.metadata?.document_url || cit.location?.uri,
          text_span: cit.text_span,
        }))
      : undefined;

    return {
      id: historyMsg.message_id,
      text: historyMsg.content,
      sender: historyMsg.role,
      timestamp: new Date(historyMsg.timestamp),
      citations: mappedCitations,
      vote_status: historyMsg.vote_status,
    };
  });
};

//e6edf8de-8ada-49a9-b9b5-1b12f6895a3c

export const AiChatPage: React.FC<AiChatPageProps> = ({}) => {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isAssistantTyping, setIsAssistantTyping] = useState(false);
  const chatAreaRef = useRef<HTMLDivElement>(null); // For scrolling
  const [selectedSousDossierObject, setSelectedSousDossierObject] = useState<any | null>(null);
  const [currentSessionId, setCurrentSessionId] = useState<string | undefined>(undefined);
  const [documentsCount, setDocumentsCount] = useState<{current: number, total: number}>({current: 0, total: 0});
  const [currentCitation, setCurrentCitation] = useState<ApiCitation | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);
  const { isAuthenticated, tokens } = useContext(AuthContext);
  const hasValidAccessToken = Boolean(tokens?.AccessToken && tokens.AccessToken.length > 0);
  const { data: userData } = useGetUserAttributesQuery({
    enabled: isAuthenticated && hasValidAccessToken,
  });
  const navigate = useNavigate();
  const { folderId, sousDossierId } = useParams();
  const isOutlook = isRunningInOutlook();
  const client_id = userData?.crpcen;
  const [openDocumentsInViewer, setOpenDocumentsInViewer] = useState<DocumentTabProps[]>([]);
  const [activeViewerDocumentId, setActiveViewerDocumentId] = useState<string | null>(null);
  const [isViewerPanelOpen, setIsViewerPanelOpen] = useState<boolean>(false);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  const [indexingStatus, setIndexingStatus] = useState<{
    all_documents_indexed: boolean;
    indexed_documents: number;
    total_documents: number;
    pending_documents: number;
    failed_documents: number;
    documents: Array<{
      input_id: string;
      input_original_filename: string;
      input_classifier_rename: string;
      step_kb_index_status: string;
      step_kb_index_errors: string[];
    }>;
  } | null>(null);
  const indexingStatusRef = useRef(indexingStatus);
  const [isCheckingIndexing, setIsCheckingIndexing] = useState(false);

  const [preloadedUrls, setPreloadedUrls] = useState<Set<string>>(new Set());
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(true);

  // WhatsApp-style inline history states
  const [historySessions, setHistorySessions] = useState<SessionHistoryResponseData[]>([]);
  const [isLoadingHistorySessions, setIsLoadingHistorySessions] = useState(false);
  const [allSessionsLoaded, setAllSessionsLoaded] = useState(false);
  const [currentSessionStartIndex, setCurrentSessionStartIndex] = useState<number | null>(null);
  const [collapsedMessages, setCollapsedMessages] = useState<Set<string>>(new Set());
  const [expandedSessions, setExpandedSessions] = useState<Set<string>>(new Set());

  const autoOpenedMessageIdRef = useRef<string | null>(null);

  // Inline autocomplete suggestion
  const inlineSuggestion = useMemo(() => getInlineSuggestion(message), [message]);

  const handleAcceptSuggestion = useCallback(() => {
    if (inlineSuggestion) {
      setMessage((prev) => prev + inlineSuggestion);
    }
  }, [inlineSuggestion]);

  const handleLoadHistorySession = useCallback((sessionId: string) => {
    setCurrentSessionId(sessionId);
    setIsHistoryPanelOpen(false);
  }, []);

  /**
   * Load previous sessions and prepend their messages above current messages
   */
  const loadPreviousSessions = useCallback(async () => {
    if (!folderId || !sousDossierId || allSessionsLoaded) {
      return;
    }

    setIsLoadingHistorySessions(true);
    
    try {
      // 1. Fetch list of sessions (excluding current one)
      const sessionsListResponse = await getChatSessionsList({
        dossier_id: folderId,
        sous_dossier_id: sousDossierId,
        limit: 10, // Load 10 previous sessions
      });

      if (!sessionsListResponse.sessions || sessionsListResponse.sessions.length === 0) {
        setAllSessionsLoaded(true);
        return;
      }

      // Filter out current session if it exists in the list
      const previousSessions = sessionsListResponse.sessions.filter(
        session => session.session_id !== currentSessionId
      );

      if (previousSessions.length === 0) {
        setAllSessionsLoaded(true);
        return;
      }

      // 2. Fetch full messages for each session
      const sessionsWithMessages: Array<{
        sessionData: SessionHistoryResponseData;
        sessionSummary: ChatSessionSummary;
      }> = [];

      for (const sessionSummary of previousSessions) {
        try {
          const response: { data: SessionHistoryResponseData } = await apiClient.get(
            `/chat/${sessionSummary.session_id}`
          );
          if (response.data && response.data.messages) {
            sessionsWithMessages.push({
              sessionData: response.data,
              sessionSummary: sessionSummary,
            });
          }
        } catch (error) {
          console.error(`Error loading session ${sessionSummary.session_id}:`, error);
          // Continue with other sessions
        }
      }

      // 3. Map all messages and prepend them
      const allHistoryMessages: Message[] = [];
      
      // Sort sessions by date (oldest first, so they appear at the top)
      sessionsWithMessages.sort((a, b) => 
        new Date(a.sessionData.created_on).getTime() - new Date(b.sessionData.created_on).getTime()
      );

      // Add messages from each session with dividers only when date changes
      let lastDate: string | null = null;
      
      sessionsWithMessages.forEach((sessionWithMsgs, index) => {
        const currentSessionId = sessionWithMsgs.sessionData.session_id;
        const currentDate = new Date(sessionWithMsgs.sessionData.created_on).toDateString();
        
        // Add a session divider only if the date is different from the previous session
        if (currentDate !== lastDate) {
          const dividerMessage: Message = {
            id: `divider-${currentDate}-${currentSessionId}`,
            text: '', // Will be rendered as SessionDivider
            sender: 'divider' as any,
            timestamp: new Date(sessionWithMsgs.sessionData.created_on),
            sessionDate: sessionWithMsgs.sessionData.created_on,
            messageCount: sessionWithMsgs.sessionSummary.message_count,
            firstQuestion: sessionWithMsgs.sessionSummary.first_question,
            sessionId: currentSessionId,
          };
          allHistoryMessages.push(dividerMessage);
          lastDate = currentDate;
        }
        else {
          const dividerMessage: Message = {
            id: `divider-${currentDate}-${currentSessionId}`,
            text: '', // Will be rendered as SessionDivider
            sender: 'divider' as any,
            timestamp: new Date(sessionWithMsgs.sessionData.created_on),
            sessionDate: sessionWithMsgs.sessionData.created_on,
            messageCount: sessionWithMsgs.sessionSummary.message_count,
            firstQuestion: sessionWithMsgs.sessionSummary.first_question,
            sessionId: currentSessionId,
            showDateText: false,
          };
          allHistoryMessages.push(dividerMessage);
          lastDate = currentDate;
        }

        // Add all messages from this session
        const sessionMessages = mapSessionHistoryToMessages(sessionWithMsgs.sessionData);
        
        // Mark messages as historical and initially collapse assistant responses
        const markedMessages = sessionMessages.map(msg => ({
          ...msg,
          isHistorical: true,
          isCollapsed: msg.sender === 'assistant', // Collapse assistant messages by default
          sessionId: currentSessionId, // Track which session this message belongs to
        }));
        
        // Collect assistant message IDs for initial collapsed state
        sessionMessages.forEach(msg => {
          if (msg.sender === 'assistant') {
            setCollapsedMessages(prev => new Set([...prev, msg.id]));
          }
        });
        
        allHistoryMessages.push(...markedMessages);
      });

      // Store the sessions data
      setHistorySessions(sessionsWithMessages.map(s => s.sessionData));

      // 4. Prepend history messages to current messages
      setMessages(prevMessages => {
        // Add current session divider if we have current messages
        if (prevMessages.length > 0) {
          const currentDivider: Message = {
            id: 'divider-current-session',
            text: '',
            sender: 'divider' as any,
            timestamp: new Date(),
            isCurrentSession: true,
          };
          return [...allHistoryMessages, currentDivider, ...prevMessages];
        }
        return [...allHistoryMessages, ...prevMessages];
      });

      // Track where current session starts for scroll management
      setCurrentSessionStartIndex(allHistoryMessages.length);
      setAllSessionsLoaded(true);

      // Scroll to current session divider after a short delay to allow rendering
      setTimeout(() => {
        const currentSessionDivider = document.getElementById('divider-current-session');
        if (currentSessionDivider && chatAreaRef.current) {
          currentSessionDivider.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);

    } catch (error) {
      console.error('Error loading previous sessions:', error);
      // Optionally show an error message to user
    } finally {
      setIsLoadingHistorySessions(false);
    }
  }, [folderId, sousDossierId, currentSessionId, allSessionsLoaded]);

  /**
   * Handle showing history - loads previous sessions inline
   */
  const handleShowHistory = useCallback(async () => {
    // If history is already loaded, toggle it off
    if (allSessionsLoaded) {
      // Remove historical messages and reset states
      setMessages(prevMessages => 
        prevMessages.filter(msg => !msg.isHistorical && msg.sender !== 'divider')
      );
      setAllSessionsLoaded(false);
      setHistorySessions([]);
      setCollapsedMessages(new Set());
      setExpandedSessions(new Set());
      setCurrentSessionStartIndex(null);
      return;
    }
    
    // Otherwise load history
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    // Load previous sessions
    await loadPreviousSessions();
  }, [allSessionsLoaded, loadPreviousSessions]);

  /**
   * Toggle collapse state for historical assistant messages
   */
  const handleToggleCollapse = useCallback((messageId: string) => {
    setCollapsedMessages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(messageId)) {
        newSet.delete(messageId);
      } else {
        newSet.add(messageId);
      }
      return newSet;
    });
  }, []);

  /**
   * Toggle entire session expansion (all messages in that session)
   */
  const handleToggleSession = useCallback((sessionId: string) => {
    setExpandedSessions(prev => {
      const newSet = new Set(prev);
      const isExpanding = !newSet.has(sessionId);
      
      if (isExpanding) {
        newSet.add(sessionId);
        // Uncollapse all messages in this session
        setCollapsedMessages(prevCollapsed => {
          const newCollapsed = new Set(prevCollapsed);
          messages.forEach(msg => {
            if (msg.sessionId === sessionId && msg.sender === 'assistant') {
              newCollapsed.delete(msg.id);
            }
          });
          return newCollapsed;
        });
      } else {
        newSet.delete(sessionId);
        // Collapse all messages in this session
        setCollapsedMessages(prevCollapsed => {
          const newCollapsed = new Set(prevCollapsed);
          messages.forEach(msg => {
            if (msg.sessionId === sessionId && msg.sender === 'assistant') {
              newCollapsed.add(msg.id);
            }
          });
          return newCollapsed;
        });
      }
      
      return newSet;
    });
  }, [messages]);

  const isAssistantTypingRef = useRef(isAssistantTyping);
  useEffect(() => {
    isAssistantTypingRef.current = isAssistantTyping;
  }, [isAssistantTyping]);

  const skipNextSessionFetchRef = useRef(false);

  useEffect(() => {
    const getChatSession = async () => {
      if (!currentSessionId) return;
      if (skipNextSessionFetchRef.current) {
        skipNextSessionFetchRef.current = false;
        return;
      }

      setIsLoadingHistory(true);
      try {
        const response: { data: SessionHistoryResponseData } = await apiClient.get(`/chat/${currentSessionId}`);

        if (response.data) {
          const mappedMessages = mapSessionHistoryToMessages(response.data);
          setMessages(mappedMessages);
        }
      } catch (error) {
        // Preserve local messages on network error
      } finally {
        setIsLoadingHistory(false);
      }
    };

    if (currentSessionId) {
      getChatSession();
    } else {
      setMessages([]);
    }
  }, [currentSessionId]);


  const handleSourceClick = (clickedCitation: ApiCitation, citationIndex: number, messageIdOfClickedCitation: string) => {
    const documentUrlOfClickedCitation = clickedCitation.displayUrl || clickedCitation.location?.uri;
    if (!documentUrlOfClickedCitation) return;

    const messageContainingClickedCitation = messages.find(msg => msg.id === messageIdOfClickedCitation);
    if (!messageContainingClickedCitation) return;

    // 1. Set the message of the clicked citation as active and set the specific citation
    setActiveMessageId(messageIdOfClickedCitation);
    setCurrentCitation(clickedCitation);

    // 2. Re-populate openDocumentsInViewer based on ALL unique sources from this newly active message
    const uniqueTabsMap = new Map<string, DocumentTabProps>();
    let uniqueTabIndex = 0;

    if (messageContainingClickedCitation.citations) {
      messageContainingClickedCitation.citations.forEach((citationInLoop) => {
        const loopDocUrl = citationInLoop.displayUrl || citationInLoop.location?.uri;
        if (!loopDocUrl) return; // Skip if no URL

        const loopPageNumber = citationInLoop.metadata?.['page_number'] || 1;
        const loopInputRenamed = citationInLoop.metadata?.['input_original_filename'] || '';
        const loopTypeDocument = citationInLoop.metadata?.type_document;

        const viewerTabIdForLoopItem = generateViewerTabId(loopDocUrl, loopPageNumber);

        if (!uniqueTabsMap.has(viewerTabIdForLoopItem)) {
          uniqueTabsMap.set(viewerTabIdForLoopItem, {
            id: viewerTabIdForLoopItem,
            index: uniqueTabIndex++, // This index is the 0-based order of unique sources in this message
            pdfUrl: loopDocUrl,
            title: loopInputRenamed,
            pageNumber: loopPageNumber,
            type_document: loopTypeDocument,
            messageId: messageIdOfClickedCitation, // Associate tab with this message
          });
        }
      });
    }

    const newTabsForMessage: DocumentTabProps[] = [];
    uniqueTabsMap.forEach(value => newTabsForMessage.push(value));
    setOpenDocumentsInViewer(newTabsForMessage);

    // 3. Set the active document tab in the viewer to the one that was actually clicked
    const pageNumberOfClickedCitation = clickedCitation.metadata?.['page_number'] || 1;
    const viewerTabIdOfClickedCitation = generateViewerTabId(documentUrlOfClickedCitation, pageNumberOfClickedCitation);
    setActiveViewerDocumentId(viewerTabIdOfClickedCitation);

    // 4. Ensure viewer panel is open if there are tabs to show
    if (newTabsForMessage.length > 0) {
      setIsViewerPanelOpen(true);
    } else {
      // This case should be rare if a valid citation was clicked, but handle defensively
      setIsViewerPanelOpen(false);
      setActiveViewerDocumentId(null);
    }
  };

  const handleMessageBodyClick = useCallback((messageId: string, forceOpen?: boolean) => {
    if (!forceOpen && activeMessageId === messageId && isViewerPanelOpen) {
      return;
    }

    const message = messages.find(msg => msg.id === messageId);
    if (!message || !message.citations || message.citations.length === 0) {
      if (activeMessageId === messageId) {
        setIsViewerPanelOpen(false);
        setActiveMessageId(null);
        setOpenDocumentsInViewer([]);
        setCurrentCitation(null);
      }
      return;
    }

    setActiveMessageId(messageId);
    setCurrentCitation(message.citations[0]);
    const uniqueTabsMap = new Map<string, DocumentTabProps>();
    let firstNewTabId: string | null = null;
    let uniqueTabIndex = 0;

    message.citations.forEach((citation) => {
      const documentUrl = citation.displayUrl || citation.location?.uri;
      if (!documentUrl) return;
      const pageNumber = citation.metadata?.['page_number'] || 1;
      const input_rename = citation.metadata?.['input_original_filename'] || '';
      const type_document = citation.metadata?.type_document;

      const viewerTabId = generateViewerTabId(documentUrl, pageNumber);

      if (!uniqueTabsMap.has(viewerTabId)) {
        if (!firstNewTabId) {
          firstNewTabId = viewerTabId;
        }
        uniqueTabsMap.set(viewerTabId, {
          id: viewerTabId,
          index: uniqueTabIndex++,
          pdfUrl: documentUrl,
          title: input_rename,
          pageNumber: pageNumber,
          type_document: type_document,
          messageId: messageId,
        });
      }
    });

    const newTabsForMessage: DocumentTabProps[] = [];
    uniqueTabsMap.forEach(value => newTabsForMessage.push(value));

    setOpenDocumentsInViewer(newTabsForMessage);

    if (newTabsForMessage.length > 0) {
      setActiveViewerDocumentId(firstNewTabId || newTabsForMessage[0].id);
      if (!isViewerPanelOpen || forceOpen) {
          setIsViewerPanelOpen(true);
      }
    } else {
      setIsViewerPanelOpen(false);
      setActiveViewerDocumentId(null);
    }
  }, [
    messages,
    activeMessageId,
    isViewerPanelOpen,
  ]);

  const handleUserMessageClick = (messageId: string) => {
    setActiveMessageId(messageId);
  };


  const handleViewerTabChange = (
    newActiveTabId: string,
    messageIdForNewActiveDoc: string
  ) => {
    setActiveViewerDocumentId(newActiveTabId);
    setActiveMessageId(messageIdForNewActiveDoc);

    const originalMessage = messages.find(msg => msg.id === messageIdForNewActiveDoc);
    const activeTabInfo = openDocumentsInViewer.find(doc => doc.id === newActiveTabId);

    if (originalMessage && originalMessage.citations && activeTabInfo) {
      const newCurrentApiCitation = originalMessage.citations.find(
        (cit) => {
          const citUrl = cit.displayUrl || cit.location?.uri;
          const citPage = cit.metadata?.['page_number'] || 1;
          return citUrl === activeTabInfo.pdfUrl && citPage === activeTabInfo.pageNumber;
        }
      );
      setCurrentCitation(newCurrentApiCitation || null);
    } else {
      setCurrentCitation(null);
    }
  };

  const handleViewerTabClose = (documentIdToClose: string) => {
    setOpenDocumentsInViewer(prevDocs => {
      const updatedDocs = prevDocs.filter(doc => doc.id !== documentIdToClose)
                                 .map((doc, idx) => ({ ...doc, index: idx }));

      if (activeViewerDocumentId === documentIdToClose) {
        if (updatedDocs.length > 0) {
          const nextActiveTab = updatedDocs[0];
          setActiveViewerDocumentId(nextActiveTab.id);
          const originalMessage = messages.find(msg => msg.id === nextActiveTab.messageId);
          if (originalMessage && originalMessage.citations) {
            const newCurrentApiCitation = originalMessage.citations.find(
              (cit) => {
                const citUrl = cit.displayUrl || cit.location?.uri;
                const citPage = cit.metadata?.['page_number'] || 1;
                return citUrl === nextActiveTab.pdfUrl && citPage === nextActiveTab.pageNumber;
              }
            );
            setCurrentCitation(newCurrentApiCitation || null);
            setActiveMessageId(nextActiveTab.messageId);
          } else {
            setCurrentCitation(null);
          }
        } else {
          setActiveViewerDocumentId(null);
          setCurrentCitation(null);
        }
      }
      return updatedDocs;
    });
  };

  const handleCloseEntireViewer = () => {
    setOpenDocumentsInViewer([]);
    setActiveViewerDocumentId(null);
    setIsViewerPanelOpen(false);
    setActiveMessageId(null);
    setCurrentCitation(null);
  };

  /**
   * @description Isolate the sous-dossier in the select from the control center.
   * This is the sous-dossier of which the documents are being displayed.
   */
  useEffect(() => {
    const fetchFolders = async () => {
      if(!folderId || !sousDossierId) return;
      const subfolders = await getSubFolders(folderId);
      if (subfolders) {
          const subfolder = subfolders.sous_dossiers.find((f) => f.sous_dossier_id === sousDossierId);
          if (subfolder) {
              setSelectedSousDossierObject(subfolder);
              // const ragStatus = await getSousDossierRagStatus({dossier_id: folderId, sous_dossier_id: sousDossierId});
              // setDocumentsCount({current: ragStatus.report.total_number_of_inputs_indexed, total: ragStatus.report.total_number_of_inputs});
          }
        }
    }
    fetchFolders();
  }, [folderId, sousDossierId]);

  // Sync ref with state
  useEffect(() => {
    indexingStatusRef.current = indexingStatus;
  }, [indexingStatus]);

  /**
   * @description Check if all documents in the sous-dossier are indexed
   * Polls every 10 seconds until indexation is complete
   */
  useEffect(() => {
    if (!folderId || !sousDossierId) return;

    let pollInterval: NodeJS.Timeout | null = null;
    let isMounted = true;

    const checkIndexingStatus = async () => {
      try {
        const status = await getSousDossierIndexingStatus({
          dossier_id: folderId,
          sous_dossier_id: sousDossierId
        });

        if (!isMounted) return;

        const newStatus = {
          all_documents_indexed: status.all_documents_indexed,
          indexed_documents: status.indexed_documents,
          total_documents: status.total_documents,
          pending_documents: status.pending_documents,
          failed_documents: status.failed_documents,
          documents: status.documents ?? []
        };

        setIndexingStatus(newStatus);
        indexingStatusRef.current = newStatus;

        // Stop polling once all documents are indexed
        if (status.all_documents_indexed && pollInterval) {
          clearInterval(pollInterval);
          pollInterval = null;
        }
      } catch (error) {
        console.error('Error checking indexing status:', error);
      }
    };

    // Initial check
    checkIndexingStatus();

    // Poll every 10 seconds to update status
    pollInterval = setInterval(() => {
      checkIndexingStatus();
    }, 10000);

    // Cleanup
    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [folderId, sousDossierId]);

  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages, isAssistantTyping]);

  const abortControllerRef = useRef<AbortController | null>(null);

  const handleSendMessage = async (messageOverride?: string) => {
    const trimmedMessage = (messageOverride ?? message).trim();
    if (!trimmedMessage) return;

    if (isAssistantTyping) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: trimmedMessage,
      sender: 'user',
      timestamp: new Date(),
    };
    setMessages((prevMessages) => [...prevMessages, userMessage]);
    setMessage('');

    setIsAssistantTyping(true);

    // Create a new AbortController for this request
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const payload: ChatAskBackendPayload = {
      message: trimmedMessage,
      client_id: client_id as string,
      dossier_id: folderId,
      sous_dossier_id: sousDossierId,
      chat_session_id: currentSessionId,
    };

    try {
      const response: ChatBackendResponse = await askChatQuestion(payload, { signal: controller.signal });

      setCurrentSessionId(response.session_id);
      skipNextSessionFetchRef.current = true;
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response.message,
        sender: 'assistant',
        timestamp: new Date(),
        citations: response.citations as ApiCitation[],
      };
      setMessages((prevMessages) => [...prevMessages, assistantMessage]);
    } catch (error: any) {
      // Gracefully handle the cancellation without showing an error
      if (error.name === 'AbortError' || abortControllerRef.current === null) {
        console.log('Chat request was cancelled.');
        return;
      }
       // Log the error to the backend
       if (currentSessionId) {
        logChatError({
          client_id: client_id as string,
          dossier_id: folderId,
          sous_dossier_id: sousDossierId,
          chat_session_id: currentSessionId,
          error_details: {
            message: error.message || "An unknown error occurred during sendMessage.",
            stack: error.stack,
            context: "handleSendMessage"
          }
        });
      }
      const errorMessage: Message = {
            id: (Date.now() + 1).toString(),
            text: `Erreur: Impossible de récupérer la réponse de l'assistant. Veuillez réessayer.`,
            sender: 'assistant',
            timestamp: new Date(),
            folderId: folderId,
            sousDossierId: sousDossierId,
        };
      setMessages((prevMessages) => [...prevMessages, errorMessage]);
    } finally {
      setIsAssistantTyping(false);
      setTimeout(() => {
        messageInputRef.current?.focus();
      }, 1000);
      // Ensure the controller ref is cleared once the request is complete
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  };

  const handleSuggestedQuestionClick = useCallback(
    (question: string) => {
      if (isAssistantTyping) return;
      setIsSuggestionsOpen(false);
      handleSendMessage(question);
    },
    [isAssistantTyping]
  );

const handleClearChat = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setMessages([]);
    setMessage('');
    setIsAssistantTyping(false);
    setCurrentSessionId(undefined);
    setOpenDocumentsInViewer([]);
    setIsViewerPanelOpen(false);
  }

  const handleExpandChat = () => {
    if (isOutlook) {
      window.open(`/#/folder/${folderId}/${sousDossierId}/chat`, '_blank', 'noopener,noreferrer');
    } else {
      navigate("/");
    }
  }
  const handleCloseChat = () => navigate("/");

  const showWelcome = messages.length === 0 && !isAssistantTyping && !isLoadingHistory;

  const tooltipTitles = {
    clear: 'Nettoyer le chat',
    expand: 'Ouvrir dans le navigateur',
    close: 'Fermer le chat',
    send: "Vous devez d'abord rédiger une question",
    history: 'Historique des conversations',
    suggestions: 'Suggestions de questions',
  };

  useEffect(() => {
    const processPrefetchesSequentially = async () => {
      const allPotentialUrls = new Set<string>();
      messages.forEach(message => {
        if (message.citations) {
          message.citations.forEach(citation => {
            const url = citation.displayUrl || citation.location?.uri;
            if (url) {
              allPotentialUrls.add(url);
            }
          });
        }
      });

      const urlsToActuallyPrefetch = Array.from(allPotentialUrls).filter(url => !preloadedUrls.has(url));

      if (urlsToActuallyPrefetch.length > 0) {
        setPreloadedUrls(prev => new Set([...prev, ...urlsToActuallyPrefetch]));
        for (const url of urlsToActuallyPrefetch) {
          try {
            const response = await fetch(url, { method: 'GET', mode: 'cors' });
            if (response.ok) {
            } else {
            }
          } catch (error) {
            console.warn(`Sequential prefetch: Error preloading ${url}:`, error);
          }
        }
      }
    };
    if (messages.length > 0) {
        processPrefetchesSequentially();
    }
  }, [messages, preloadedUrls]);

  useEffect(() => {
    if (isViewerPanelOpen) {
      const documentViewerPanel = document.getElementById('container');
      if (documentViewerPanel) {
        documentViewerPanel.style.justifyContent = 'flex-end';
      }
    } else {
      const documentViewerPanel = document.getElementById('container');
      if (documentViewerPanel) {
        documentViewerPanel.style.justifyContent = 'center';
      }
    }
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [isViewerPanelOpen]);

  useEffect(() => {
    let messageToAutoOpen: Message | null = null;
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (msg.sender === 'assistant' && msg.citations && msg.citations.length > 0) {
        messageToAutoOpen = msg;
        break;
      }
    }

    if (messageToAutoOpen) {

      if (messageToAutoOpen.id !== autoOpenedMessageIdRef.current) {
        handleMessageBodyClick(messageToAutoOpen.id, true);
        autoOpenedMessageIdRef.current = messageToAutoOpen.id;
      }
    }
  }, [messages, handleMessageBodyClick]);

  const chatContainerBorderRadius = isViewerPanelOpen ? '20px 0px 0px 20px' : '20px';
  const inputAreaBorderRadius = isViewerPanelOpen ? '0px 0px 0px 20px' : '0px 0px 20px 20px';

  // Scroll to the active message when it changes
  useEffect(() => {
    if (chatAreaRef.current && activeMessageId) {
      const activeMessageElement = document.getElementById(activeMessageId);
      if (activeMessageElement) {
        activeMessageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [activeMessageId]);

  const handleCopyToClipboard = async (textToCopy: string) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      // Consider adding a snackbar here for user feedback
    } catch (err) {
      console.error('Failed to copy text: ', err);
      // Consider adding a snackbar here for user feedback
    }
  };

  const handleVote = async (messageId: string, vote: 'good' | 'bad') => {
    setMessages(prevMessages => prevMessages.map(msg =>
      msg.id === messageId ? { ...msg, vote_status: vote } : msg
    ));
    try {
      await voteMessage({
        chat_session_id: currentSessionId as string,
        message_id: messageId,
        vote_status: vote,
      });
    } catch (error) {
      console.error('Failed to vote:', error);
    }
  };

  const handleRetryMessage = async (messageIdToRetry: string) => {
    const assistantMessageIndex = messages.findIndex(msg => msg.id === messageIdToRetry && msg.sender === 'assistant');
    if (assistantMessageIndex === -1) {
      console.warn("Could not find assistant message to retry:", messageIdToRetry);
      return;
    }

    let userPromptText = 'This is a retry. The user prompt is: ';
    // Iterate backwards from the assistant message to find the last user message
    for (let i = assistantMessageIndex - 1; i >= 0; i--) {
      if (messages[i].sender === 'user') {
        const promptSource = messages[i].rawText || messages[i].text;
        if (typeof promptSource === 'string') {
          userPromptText += promptSource;
        } else {
          console.warn("Cannot retry: User prompt for the message is not a string.");
          // Optionally, notify the user here
          return;
        }
        break;
      }
    }

    if (!userPromptText) {
      console.warn("Could not find the original user prompt for retrying message:", messageIdToRetry);
      // Optionally, notify the user here
      return;
    }

    // 1. Update UI: Remove old assistant message and show typing indicator
    // It's generally good to remove any messages that came *after* the one being retried as well.
    setMessages(prevMessages => prevMessages.slice(0, assistantMessageIndex));
    setIsAssistantTyping(true);

    // 2. Prepare payload for the API call
    const payload: ChatAskBackendPayload = {
      message: userPromptText,
      client_id: client_id as string, // Ensure client_id is available in this component's scope
      dossier_id: folderId,       // Ensure folderId is available
      sous_dossier_id: sousDossierId, // Ensure sousDossierId is available
      chat_session_id: currentSessionId, // Use currentSessionId
    };

    // 3. Call the backend
    try {
      const response: ChatBackendResponse = await askChatQuestion(payload);
      // Update session ID if it changed (it might for a new turn in some systems)
      if (response.session_id) {
        setCurrentSessionId(response.session_id);
      }

      const newAssistantMessage: Message = {
        id: `${Date.now()}-assistant-retry`, // More unique ID
        text: response.message,
        sender: 'assistant',
        timestamp: new Date(),
        citations: response.citations as ApiCitation[],
        folderId: folderId,
        sousDossierId: sousDossierId,
      };
      setMessages(prevMessages => [...prevMessages, newAssistantMessage]);

    } catch (error) {
      console.error('Error during retry:', error);
      const errorMessage: Message = {
        id: `${Date.now()}-error-retry`, // More unique ID
        text: `Erreur lors de la nouvelle tentative. Veuillez réessayer.`,
        sender: 'assistant',
        timestamp: new Date(),
        folderId: folderId,
        sousDossierId: sousDossierId,
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
    } finally {
      setIsAssistantTyping(false);
    }
  };

  return (
    <PageContainer
      sx={{
        justifyContent: isViewerPanelOpen ? 'flex-end' : 'center',
        alignItems: 'stretch', // Ensure children can take full height

      }}
    >
      {isViewerPanelOpen ? (
        <Box
          sx={{
            display: 'flex',
            width: '90%',
            height: '100%',
            margin: "0px -10px"
          }}
        >
          <ChatContainer
            sx={{
              flex: '1 1 60%',
              minWidth:  0,
              maxWidth: 'none',
              height: '100%',
              borderRadius: chatContainerBorderRadius,
            }}
          >
            <Header>
              <TitleBlock>
                <Typography title={`${client_id || ''} - ${userData?.client_name || ''}`}>
                  {client_id} - {userData?.client_name}
                </Typography>
                <HeaderTitle title={selectedSousDossierObject?.dossier_name}>{selectedSousDossierObject?.dossier_name}</HeaderTitle>
                <HeaderSubtitle title={selectedSousDossierObject?.sous_dossier_name}>{selectedSousDossierObject?.sous_dossier_nature} - {selectedSousDossierObject?.sous_dossier_name}</HeaderSubtitle>
              </TitleBlock>
              <HeaderIcons>
                <StyledTooltipIcon tooltipTitle={allSessionsLoaded ? 'Masquer l\'historique' : tooltipTitles.history}>
                  <IconButton
                    size='small'
                    onClick={handleShowHistory}
                    disabled={isLoadingHistorySessions}
                    sx={{
                      padding: '4px',
                      color: allSessionsLoaded ? '#2563EB' : '#6B7280',
                      backgroundColor: allSessionsLoaded ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                      '&:hover': {
                        color: '#2563EB',
                        backgroundColor: allSessionsLoaded ? 'rgba(37, 99, 235, 0.15)' : 'transparent',
                      },
                    }}
                  >
                    <HistoryIcon sx={{ fontSize: '20px' }} />
                  </IconButton>
                </StyledTooltipIcon>
                <StyledTooltipIcon tooltipTitle={tooltipTitles.clear}>
                  <HoverableActionIcon
                    defaultIconSrc={balaisIcon}
                    hoverIconSrc={balaisIconHover}
                    altText={tooltipTitles.clear}
                    onClick={handleClearChat}
                  />
                </StyledTooltipIcon>
                {isOutlook && (
                  <StyledTooltipIcon tooltipTitle={tooltipTitles.expand}>
                    <HoverableActionIcon
                      defaultIconSrc={groupIcon}
                      hoverIconSrc={groupIconHover}
                      altText={tooltipTitles.expand}
                      onClick={handleExpandChat}
                    />
                  </StyledTooltipIcon>
                )}
                <StyledTooltipIcon tooltipTitle={tooltipTitles.close}>
                  <HoverableActionIcon
                    defaultIconSrc={crossIcon}
                    hoverIconSrc={crossIconHover}
                    altText={tooltipTitles.close}
                    onClick={handleCloseChat}
                  />
                </StyledTooltipIcon>
              </HeaderIcons>
            </Header>
            {indexingStatus && <IndexingStatusBanner indexingStatus={indexingStatus} />}
            <ChatArea ref={chatAreaRef}>
              {isLoadingHistory ? (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}><Typography>Loading history...</Typography></Box>
              ) : showWelcome ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: 2 }}>
                    <HistoryButton
                      onClick={handleShowHistory}
                      disabled={isLoadingHistorySessions}
                    >
                      <NewBadge label="Nouveau" size="small" />
                      Afficher l'historique de conversation
                    </HistoryButton>
                  </Box>
                  <WelcomeMessageContainer>
                    <WelcomeTitle>Assistant IA</WelcomeTitle>
                    {indexingStatus && indexingStatus.indexed_documents === 0 ? (
                      <WelcomeText sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                        Le chat n'est pas encore prêt. Veuillez patienter pendant l'indexation des documents...
                      </WelcomeText>
                    ) : (
                      <WelcomeText>
                        Posez votre question en langage naturel : notre intelligence
                        artificielle analyse instantanément les documents de votre
                        sous-dossier et vous fournit une réponse claire, sourcée et prête à
                        l'emploi.
                      </WelcomeText>
                    )}
                    {documentsCount.total !== documentsCount.current && (
                      <WelcomeText>
                        Nombre de documents disponibles : {documentsCount.current} / {documentsCount.total}
                      </WelcomeText>
                    )}
                  </WelcomeMessageContainer>
                </>
              ) : (
                <>
                  {isLoadingHistorySessions && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 2 }}>
                      <Typography sx={{ color: '#6B7280', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
                        Chargement de l'historique...
                      </Typography>
                    </Box>
                  )}
                  {messages.map((msg, index) => {
                    // Find the next assistant message for user messages
                    let nextMessageId: string | undefined;
                    let isNextMessageCollapsed: boolean | undefined;
                    if (msg.isHistorical && msg.sender === 'user') {
                      const nextMsg = messages[index + 1];
                      if (nextMsg && nextMsg.sender === 'assistant') {
                        nextMessageId = nextMsg.id;
                        isNextMessageCollapsed = collapsedMessages.has(nextMsg.id);
                      }
                    }
                    return (
                      <ChatMessage
                        folderId={folderId}
                        sousDossierId={sousDossierId}
                        key={msg.id}
                        {...msg}
                        isCollapsed={collapsedMessages.has(msg.id)}
                        onToggleCollapse={handleToggleCollapse}
                        isSessionExpanded={msg.sessionId ? expandedSessions.has(msg.sessionId) : undefined}
                        onToggleSession={handleToggleSession}
                        onSourceClick={handleSourceClick}
                        onMessageBodyClick={
                          msg.sender === 'assistant' && msg.citations && msg.citations.length > 0
                            ? () => handleMessageBodyClick(msg.id)
                            : msg.sender === 'user'
                              ? () => handleUserMessageClick(msg.id)
                              : undefined
                        }
                        isActiveMessage={activeMessageId === msg.id}
                        rawText={typeof msg.text === 'string' ? msg.text : undefined}
                        onRetry={msg.sender === 'assistant' ? handleRetryMessage : undefined}
                        onCopy={msg.sender === 'assistant' ? handleCopyToClipboard : undefined}
                        onLike={handleVote}
                        onDislike={handleVote}
                        vote_status={msg.vote_status}
                        nextMessageId={nextMessageId}
                        isNextMessageCollapsed={isNextMessageCollapsed}
                      />
                    );
                  })}
              {isAssistantTyping && <ChatMessage id="typing" text="" sender="typing_indicator" onSourceClick={handleClearChat} />}
            </>
            )}
            </ChatArea>
            <Collapse in={isSuggestionsOpen}>
              <Box
                sx={{
                  borderBottom: '1px solid #E4E7EC',
                  backgroundColor: '#FAFBFC',
                }}
              >
                <SuggestedQuestions
                  onQuestionClick={handleSuggestedQuestionClick}
                  disabled={isAssistantTyping || !(indexingStatus && indexingStatus.indexed_documents > 0)}
                />
              </Box>
            </Collapse>
            <InputArea sx={{ borderRadius: '0px' }}>
              <StyledTooltipIcon tooltipTitle={tooltipTitles.suggestions} placement='top'>
                <IconButton
                  size='small'
                  onClick={() => setIsSuggestionsOpen(!isSuggestionsOpen)}
                  sx={{
                    padding: '4px',
                    marginRight: '4px',
                    color: isSuggestionsOpen ? '#2563EB' : '#6B7280',
                    '&:hover': {
                      color: '#2563EB',
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <LightbulbOutlinedIcon sx={{ fontSize: '20px' }} />
                </IconButton>
              </StyledTooltipIcon>
              <InlineSuggestionInput
                inputRef={messageInputRef}
                value={message}
                onChange={setMessage}
                onSubmit={handleSendMessage}
                onAcceptSuggestion={handleAcceptSuggestion}
                suggestion={inlineSuggestion}
                disabled={isAssistantTyping}
                placeholder='Écrivez votre question...'
              />
              <StyledTooltipIcon tooltipTitle={tooltipTitles.send} placement='top' disabled={!!message.trim()}>
                <SendButton
                  onClick={() => handleSendMessage()}
                  disabled={!message.trim()}
                  hastext={message.trim() ? 'true' : 'false'}
                >
                  <ArrowUpwardRoundedIcon sx={{ color: 'white', backgroundColor: 'transparent', fontSize: '20px' }} />
                </SendButton>
              </StyledTooltipIcon>
            </InputArea>
            {isHistoryPanelOpen && folderId && sousDossierId && (
              <ChatHistoryPanel
                dossierId={folderId}
                sousDossierId={sousDossierId}
                onClose={() => setIsHistoryPanelOpen(false)}
                onSessionSelect={handleLoadHistorySession}
              />
            )}
          </ChatContainer>

          <Box
            sx={{
              flex: '1 1 60%',
              minWidth: '0',
              maxWidth: 'none',
              height: '100%',
              borderRadius: '0px 20px 20px 0px',
            }}
          >
            <DocumentViewerPanel
              openDocuments={openDocumentsInViewer}
              activeDocumentId={activeViewerDocumentId}
              onTabChange={handleViewerTabChange}
              currentCitation={currentCitation}
              onTabClose={handleViewerTabClose}
              onCloseViewer={handleCloseEntireViewer}
            />
          </Box>
        </Box>
      ) : (
        <ChatContainer
          sx={{
            flexBasis: '40%',
            minWidth: '600px',
            "@media (max-width: 1200px)": {
              minWidth: '400px',
            },
            maxWidth: 'none',
            height: '100%',
            borderRadius: chatContainerBorderRadius,
          }}
        >
          <Header>
            <TitleBlock>
              <Typography title={`${client_id || ''} - ${userData?.client_name || ''}`}>
                {client_id} - {userData?.client_name}
              </Typography>
              <HeaderTitle title={selectedSousDossierObject?.dossier_name}>{selectedSousDossierObject?.dossier_name}</HeaderTitle>
              <HeaderSubtitle title={selectedSousDossierObject?.sous_dossier_name}>{selectedSousDossierObject?.sous_dossier_nature} - {selectedSousDossierObject?.sous_dossier_name}</HeaderSubtitle>
            </TitleBlock>
            <HeaderIcons>
              <StyledTooltipIcon tooltipTitle={allSessionsLoaded ? 'Masquer l\'historique' : tooltipTitles.history}>
                <IconButton
                  size='small'
                  onClick={handleShowHistory}
                  disabled={isLoadingHistorySessions}
                  sx={{
                    padding: '4px',
                    color: allSessionsLoaded ? '#2563EB' : '#6B7280',
                    backgroundColor: allSessionsLoaded ? 'rgba(37, 99, 235, 0.1)' : 'transparent',
                    '&:hover': {
                      color: '#2563EB',
                      backgroundColor: allSessionsLoaded ? 'rgba(37, 99, 235, 0.15)' : 'transparent',
                    },
                  }}
                >
                  <HistoryIcon sx={{ fontSize: '20px' }} />
                </IconButton>
              </StyledTooltipIcon>
              <StyledTooltipIcon tooltipTitle={tooltipTitles.clear}>
                <HoverableActionIcon
                  defaultIconSrc={balaisIcon}
                  hoverIconSrc={balaisIconHover}
                  altText={tooltipTitles.clear}
                  onClick={handleClearChat}
                />
              </StyledTooltipIcon>
              {isOutlook && (
                <StyledTooltipIcon tooltipTitle={tooltipTitles.expand}>
                  <HoverableActionIcon
                    defaultIconSrc={groupIcon}
                    hoverIconSrc={groupIconHover}
                    altText={tooltipTitles.expand}
                    onClick={handleExpandChat}
                  />
                </StyledTooltipIcon>
              )}
              <StyledTooltipIcon tooltipTitle={tooltipTitles.close}>
                <HoverableActionIcon
                  defaultIconSrc={crossIcon}
                  hoverIconSrc={crossIconHover}
                  altText={tooltipTitles.close}
                  onClick={handleCloseChat}
                />
              </StyledTooltipIcon>
            </HeaderIcons>
          </Header>
          {indexingStatus && <IndexingStatusBanner indexingStatus={indexingStatus} />}
          <ChatArea ref={chatAreaRef}>
            {isLoadingHistory ? (
                <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}><Typography>Loading history...</Typography></Box>
            ) : showWelcome ? (
              <>
                <Box sx={{ display: 'flex', justifyContent: 'center', paddingTop: 2 }}>
                  <HistoryButton
                    onClick={handleShowHistory}
                    disabled={isLoadingHistorySessions}
                  >
                    <NewBadge label="Nouveau" size="small" />
                    Afficher l'historique de conversation
                  </HistoryButton>
                </Box>
                <WelcomeMessageContainer>
                  <WelcomeTitle>Assistant IA</WelcomeTitle>
                  {indexingStatus && indexingStatus.indexed_documents === 0 ? (
                    <WelcomeText sx={{ color: '#9CA3AF', fontStyle: 'italic' }}>
                      Le chat n'est pas encore prêt. Veuillez patienter pendant l'indexation des documents...
                    </WelcomeText>
                  ) : (
                    <WelcomeText>
                      Posez votre question en langage naturel : notre intelligence
                      artificielle analyse instantanément les documents de votre
                      sous-dossier et vous fournit une réponse claire, sourcée et prête à
                      l'emploi.
                    </WelcomeText>
                  )}
                  {documentsCount.total !== documentsCount.current && (
                    <WelcomeText>
                      Nombre de documents disponibles : {documentsCount.current} / {documentsCount.total}
                    </WelcomeText>
                  )}
                </WelcomeMessageContainer>
              </>
            ) : (
              <>
                {isLoadingHistorySessions && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 2 }}>
                    <Typography sx={{ color: '#6B7280', fontSize: '14px', fontFamily: 'Inter, sans-serif' }}>
                      Chargement de l'historique...
                    </Typography>
                  </Box>
                )}
                {messages.map((msg, index) => {
                  // Find the next assistant message for user messages
                  let nextMessageId: string | undefined;
                  let isNextMessageCollapsed: boolean | undefined;
                  if (msg.isHistorical && msg.sender === 'user') {
                    const nextMsg = messages[index + 1];
                    if (nextMsg && nextMsg.sender === 'assistant') {
                      nextMessageId = nextMsg.id;
                      isNextMessageCollapsed = collapsedMessages.has(nextMsg.id);
                    }
                  }
                  return (
                    <ChatMessage
                      folderId={folderId}
                      sousDossierId={sousDossierId}
                      key={msg.id}
                      {...msg}
                      isCollapsed={collapsedMessages.has(msg.id)}
                      onToggleCollapse={handleToggleCollapse}
                      isSessionExpanded={msg.sessionId ? expandedSessions.has(msg.sessionId) : undefined}
                      onToggleSession={handleToggleSession}
                      onSourceClick={handleSourceClick}
                      onMessageBodyClick={
                        msg.sender === 'assistant' && msg.citations && msg.citations.length > 0
                          ? () => handleMessageBodyClick(msg.id)
                          : msg.sender === 'user'
                            ? () => handleUserMessageClick(msg.id)
                            : undefined
                      }
                      isActiveMessage={activeMessageId === msg.id}
                      rawText={typeof msg.text === 'string' ? msg.text : undefined}
                      onRetry={msg.sender === 'assistant' ? handleRetryMessage : undefined}
                      onCopy={msg.sender === 'assistant' ? handleCopyToClipboard : undefined}
                      onLike={handleVote}
                      onDislike={handleVote}
                      vote_status={msg.vote_status}
                      nextMessageId={nextMessageId}
                      isNextMessageCollapsed={isNextMessageCollapsed}
                    />
                  );
                })}
                {isAssistantTyping && <ChatMessage id="typing" text="" sender="typing_indicator" onSourceClick={handleClearChat} />}
            </>
          )}
          </ChatArea>
          <Collapse in={isSuggestionsOpen}>
            <Box
              sx={{
                borderBottom: '1px solid #E4E7EC',
                backgroundColor: '#FAFBFC',
              }}
            >
              <SuggestedQuestions
                onQuestionClick={handleSuggestedQuestionClick}
                disabled={isAssistantTyping || !(indexingStatus && indexingStatus.indexed_documents > 0)}
              />
            </Box>
          </Collapse>
          <InputArea sx={{ borderRadius: '0px 0px 20px 20px' }}>
            <StyledTooltipIcon tooltipTitle={tooltipTitles.suggestions} placement='top'>
              <IconButton
                size='small'
                onClick={() => setIsSuggestionsOpen(!isSuggestionsOpen)}
                sx={{
                  padding: '4px',
                  marginRight: '4px',
                  color: isSuggestionsOpen ? '#2563EB' : '#6B7280',
                  '&:hover': {
                    color: '#2563EB',
                    backgroundColor: 'transparent',
                  },
                }}
              >
                <LightbulbOutlinedIcon sx={{ fontSize: '20px' }} />
              </IconButton>
            </StyledTooltipIcon>
            <InlineSuggestionInput
              inputRef={messageInputRef}
              value={message}
              onChange={setMessage}
              onSubmit={handleSendMessage}
              onAcceptSuggestion={handleAcceptSuggestion}
              suggestion={inlineSuggestion}
              disabled={isAssistantTyping || !(indexingStatus && indexingStatus.indexed_documents > 0)}
              placeholder='Écrivez votre question...'
            />
            <StyledTooltipIcon tooltipTitle={tooltipTitles.send} placement='top' disabled={!!message.trim()}>
              <SendButton
                onClick={() => handleSendMessage()}
                disabled={!message.trim()}
                hastext={message.trim() ? 'true' : 'false'}
              >
                <ArrowUpwardRoundedIcon sx={{ color: 'white', backgroundColor: 'transparent', fontSize: '20px' }} />
              </SendButton>
            </StyledTooltipIcon>
          </InputArea>
          {isHistoryPanelOpen && folderId && sousDossierId && (
            <ChatHistoryPanel
              dossierId={folderId}
              sousDossierId={sousDossierId}
              onClose={() => setIsHistoryPanelOpen(false)}
              onSessionSelect={handleLoadHistorySession}
            />
          )}
        </ChatContainer>
      )}
    </PageContainer>
  );
};

